// apps/web/src/features/call-center/components/CallCenterLeadRow.tsx
//
// One desktop grid row of the external agent's own-leads table (#1398).
// Full-row activation (`useInteractiveRow`) opens the read-only detail drawer
// from the in-memory snapshot. The call-recording cell stops propagation so
// the anchor opens the recording without also firing the row click.
//
// DANGER: external boundary — render ONLY the blind snapshot fields. Never the
// opaque `id`, no lead/public code, no server-side CRM state.
import { useTranslation } from 'react-i18next';
import type { CallCenterMyLeadsItem } from '@magimp/shared';
import { useInteractiveRow } from '../../../ui/index.js';
import { formatDateTime } from '../../../i18n/index.js';
import { GRID_TEMPLATE } from './callCenterLeadGrid.js';

const CELL: React.CSSProperties = {
  padding: '0 10px',
  minHeight: 44,
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
  color: 'var(--fg-1)',
  borderBottom: '1px solid var(--border-1)',
  minWidth: 0,
};

const TRUNCATE: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export function CallCenterLeadRow({
  lead,
  onActivate,
}: {
  lead: CallCenterMyLeadsItem;
  onActivate: () => void;
}) {
  const { t } = useTranslation('callCenter');
  const { background, handlers } = useInteractiveRow({ selected: false, onActivate });

  return (
    <div
      {...handlers}
      aria-label={lead.name || lead.phone}
      style={{
        display: 'grid',
        gridColumn: '1 / -1',
        gridTemplateColumns: GRID_TEMPLATE,
        background,
      }}
    >
      <div style={{ ...CELL, ...TRUNCATE }}>{lead.phone}</div>
      <div style={{ ...CELL, ...TRUNCATE }}>{lead.name}</div>
      <div style={{ ...CELL, ...TRUNCATE, color: 'var(--fg-2)' }}>{lead.comment ?? '—'}</div>
      <div style={CELL}>
        {lead.audioUrl ? (
          <a
            href={lead.audioUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'var(--accent-1)', textDecoration: 'none', ...TRUNCATE }}
          >
            {t('selfService.audioLink')}
          </a>
        ) : (
          <span style={{ color: 'var(--fg-3)' }}>—</span>
        )}
      </div>
      <div style={{ ...CELL, color: 'var(--fg-2)', ...TRUNCATE }}>
        {formatDateTime(lead.createdAt)}
      </div>
    </div>
  );
}
